import { type FC } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useTranslation } from "react-i18next";
import type { StreamSignalLike } from "@/partner/entities/streams";
import { interpolationFormat } from "@/partner/shared/lib";
import { StreamSignalStateType } from "@/types/graphql/partner.generated";
import { disconnectReasonKey } from "../model/eventCodes";
import { formatDuration, formatMoment } from "../model/format";
import type { MonitoringPeriod } from "../model/period";
import type { LiveInputOutages, MonitoringStatus } from "../model/types";
import { MonitoringPeriodSelect } from "./MonitoringPeriodSelect";

interface MonitoringSummaryCardProps {
  /** Сигнал «есть ли видео»; null, если показывать нечего (эфир не идёт). */
  signal: StreamSignalLike | null;
  status: MonitoringStatus | null;
  outages: LiveInputOutages | null;
  viewersOnline?: number | null;
  period: MonitoringPeriod;
  periods: MonitoringPeriod[];
  onChangePeriod: (period: MonitoringPeriod) => void;
}

// Cloudflare отдаёт протокол строчными («rtmp», «srt»); показываем
// привычные названия: вход по RTMP у нас всегда защищённый (RTMPS).
const PROTOCOL_LABELS: Record<string, string> = {
  rtmp: "RTMPS",
  srt: "SRT",
  webrtc: "WebRTC",
};

function protocolLabel(protocol: string | null | undefined): string {
  if (!protocol) return "";
  return PROTOCOL_LABELS[protocol.toLowerCase()] ?? protocol.toUpperCase();
}

const SIGNAL_COLORS: Record<
  StreamSignalStateType,
  "success" | "error" | "default"
> = {
  [StreamSignalStateType.OkType]: "success",
  [StreamSignalStateType.NoVideoType]: "error",
  [StreamSignalStateType.DisconnectedType]: "default",
};

/**
 * Сводка вкладки «Мониторинг» (спека 2026-10-24-stream-monitoring-ux,
 * часть 2, п. 1): сигнал по самому видео, состояние энкодера с причиной
 * отключения человеческими словами, зрители сейчас и итог обрывов за
 * период. Выбор периода в action карточки.
 */
export const MonitoringSummaryCard: FC<MonitoringSummaryCardProps> = ({
  signal,
  status,
  outages,
  viewersOnline,
  period,
  periods,
  onChangePeriod,
}) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;

  let signalLabel: string | null = null;
  if (signal?.state === StreamSignalStateType.OkType) {
    signalLabel = t("streams.monitoring.signal.OK");
  } else if (signal?.state === StreamSignalStateType.NoVideoType) {
    signalLabel = t("streams.monitoring.signal.NO_VIDEO", {
      time: formatMoment(signal.since, lng),
    });
  } else if (signal?.state === StreamSignalStateType.DisconnectedType) {
    signalLabel = t("streams.monitoring.signal.DISCONNECTED");
  }

  // Тот же формат, что у `{{ value, dateTime }}` в переводах стримов.
  const since = status?.since
    ? interpolationFormat(status.since, "dateTime", lng)
    : "";
  const reasonKey = status?.reason ? disconnectReasonKey(status.reason) : null;
  const reason = reasonKey ? t(reasonKey) : (status?.reason ?? "");

  // Отключённый вход без момента и причины неотличим от неизвестного
  // состояния; без причины показываем только момент.
  let inputLabel: string;
  if (!status || (!status.connected && !since && !reason)) {
    inputLabel = t("streams.monitoring.input.unknown");
  } else if (status.connected) {
    inputLabel = t("streams.monitoring.input.connected", {
      protocol: protocolLabel(status.protocol),
      since,
    });
  } else if (reason) {
    inputLabel = t("streams.monitoring.input.disconnected", { since, reason });
  } else {
    inputLabel = t("streams.monitoring.input.disconnectedNoReason", { since });
  }

  let outagesLabel: string | null = null;
  if (outages && outages.count > 0) {
    outagesLabel = t("streams.monitoring.outages.summary", {
      count: outages.count,
      total: formatDuration(t, outages.totalSeconds),
      last: outages.lastAt ? formatMoment(outages.lastAt, lng) : "",
    });
  } else if (outages) {
    outagesLabel = t("streams.monitoring.outages.none");
  }

  return (
    <Card>
      <CardHeader
        title={t("streams.monitoring.summary.title")}
        slotProps={{ title: { variant: "subtitle1" } }}
        action={
          <MonitoringPeriodSelect
            value={period}
            periods={periods}
            onChange={onChangePeriod}
          />
        }
        sx={{
          px: 2,
          pt: 2,
          pb: 0,
          flexWrap: "wrap",
          rowGap: 1,
          "& .MuiCardHeader-action": { m: 0 },
        }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {signal && signalLabel ? (
              <Tooltip title={t("streams.monitoring.signal.hint")}>
                <Chip
                  icon={<FiberManualRecordIcon />}
                  color={SIGNAL_COLORS[signal.state]}
                  label={signalLabel}
                />
              </Tooltip>
            ) : null}
            {typeof viewersOnline === "number" ? (
              <Chip
                variant="outlined"
                label={t("streams.monitoring.viewersOnline", {
                  count: viewersOnline,
                })}
              />
            ) : null}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {inputLabel}
          </Typography>
          {outagesLabel ? (
            <Typography
              variant="body2"
              color={outages && outages.count > 0 ? "error" : "text.secondary"}
            >
              {outagesLabel}
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};
